import { Component } from '@theme/component';

/**
 * Collection Tabs component
 *
 * Switches between multiple collection panels using accessible tab semantics.
 * Tab buttons carry role="tab"; panels carry role="tabpanel".
 * Inactive panels are hidden with `hidden` + `inert` to remove them from focus order.
 *
 * @extends {Component<{}>}
 */
class CollectionTabs extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
  }

  get #tabs() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
  }

  get #panels() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tabpanel"]')]);
  }

  /**
   * Activate the tab at the given index.
   *
   * @param {number} index
   */
  select(index) {
    const tabs = this.#tabs;
    const panels = this.#panels;

    tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });

    panels.forEach((panel, i) => {
      const active = i === index;
      panel.hidden = !active;
      if (active) {
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('inert', '');
      }
    });
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const tab = /** @type {HTMLElement} */ (event.target)?.closest('[role="tab"]');
    if (!tab) return;
    const index = this.#tabs.indexOf(/** @type {HTMLElement} */ (tab));
    if (index !== -1) this.select(index);
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const tabs = this.#tabs;
    if (tabs.length === 0) return;

    const current = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    const delta = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];

    let next = current;
    if (delta !== undefined) {
      next = (current + delta + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      next = 0;
    } else if (event.key === 'End') {
      next = tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    this.select(next);
    tabs[next].focus();
  };
}

if (!customElements.get('collection-tabs-component')) {
  customElements.define('collection-tabs-component', CollectionTabs);
}
